import { NgModule } from '@angular/core';
import { APP_INITIALIZER } from '@angular/core';
import { Router } from '@angular/router';
import { AppRoutingModule } from './app-routing.module';
import { HttpClientModule, HttpClientXsrfModule } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { NgbPaginationModule } from '@ng-bootstrap/ng-bootstrap';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { NgbNavModule } from '@ng-bootstrap/ng-bootstrap';
import { NgbTypeaheadModule } from '@ng-bootstrap/ng-bootstrap';

import { NgSelectModule } from '@ng-select/ng-select';

import { ToastrModule } from 'ngx-toastr';

// AG Grid grid library
import { AgGridModule } from 'ag-grid-angular';
// Import AG Grid Modules
import { ModuleRegistry } from 'ag-grid-community';
import { ValidationModule, ClientSideRowModelModule, TextFilterModule, ColumnAutoSizeModule } from 'ag-grid-community';
import { EventApiModule, TooltipModule, PaginationModule, RowSelectionModule, CellStyleModule } from 'ag-grid-community';
import { ColumnApiModule } from 'ag-grid-community';
// Register the necessary modules
ModuleRegistry.registerModules([
	ValidationModule,
	ClientSideRowModelModule,
	TextFilterModule,
	ColumnAutoSizeModule,
	EventApiModule,
	TooltipModule,
	PaginationModule,
	RowSelectionModule,
	CellStyleModule,
	ColumnApiModule
]);


import { NgBlockUI, BlockUI, BlockUIModule } from 'ng-block-ui';

import { AngularCropperjsModule } from 'angular-cropperjs';

import { NgIdleKeepaliveModule } from '@ng-idle/keepalive';

import { ConfigService } from './services/config.service';
import { UserService } from './services/user.service';
import { AnalyticsService } from './services/analytics.service';
import { LoginService } from './login/services/login.service';
import { FeatureService } from './services/feature.service';


// Factory function for APP_INITIALIZER
export function initializeApp (
	configService: ConfigService,
	userService: UserService,
	analyticsService: AnalyticsService,
	loginService: LoginService,
	featureService: FeatureService,
	router: Router
) {
	return () => {
		return configService.waitForConfigLoaded().then(async () => {
			await featureService.loadFeatureConfig();
			// Check if the URL includes "autologin"
			if (!window.location.pathname.includes('autologin') && !window.location.pathname.includes('assets')) {
				return userService.whenGetUserdata().then(
					async (res) => {
						return userService.processToken().then(async () => {
							await userService.processUserDetails(res);
							userService.loadLocalization();
							userService.broadcastEvent.emit('loggedin');
						});
					},
					(err) => {
						// Log in with public user
						if (configService.getConfig().auth.public.autoEnabled) {
							return userService.attemptPublicUserLogin().then(() => {
								return userService.processToken().then(() => {
									userService.broadcastEvent.emit('loggedin');
								});
							}).catch((err) => {
								userService.loadLocalization();
								configService.getConfig().auth.public.autoEnabled = false;
								loginService.showLogin();
								return Promise.resolve();
							});
						} else {
							// Load default "en" localization
							userService.user.locale = 'en';
							userService.loadLocalization();
							if (!router.url.includes('reset')) {
								loginService.showLogin();
							}
							return Promise.resolve();
						}
					}
				);
			} else {
				return Promise.resolve();
			}
		});
	};
}

import { FacetHideFilterPipe } from './pipes/facet-hide-filter.pipe';
import { FacetRefineFilterPipe } from './pipes/facet-refine-filter.pipe';
import { LimitToPipe } from './pipes/limit-to.pipe';
import { IsEmptyPipe } from './pipes/is-empty.pipe';
import { FormatStringPipe } from './pipes/format-string.pipe';
import { SafeHtmlPipe } from './pipes/safe-html.pipe';

import { TextCollapseDirective } from './directives/text-collapse.directive';
import { CitationDirective } from './directives/citation.directive';
import { DropzoneDirective } from './directives/dropzone.directive';
import { FileinputDirective } from './directives/fileinput.directive';
import { ZoomImageDirective } from './directives/zoom-image.directive';
import { BrowsePathValidateDirective } from './directives/browsepathvalidate.directive';
import { OverflowHideDirective } from './directives/overflow-hide.directive';
import { CaSidebarDirective} from './directives/casidebar.directive';
import { AppComponent } from './app.component';
import { AboutComponent } from './components/about/about.component';
import { AlertsComponent } from './alerts/alerts.component';
import { ConfirmComponent } from './components/confirm/confirm.component';
import { MassDownloadComponent } from './download/mass-download/mass-download.component';
import { SearchTermsComponent } from './components/search-terms/search-terms.component';
import { ImageRotateComponent } from './components/image-rotate/image-rotate.component';
import { LibraryMoveComponent } from './components/library-move/library-move.component';
import { PrintResultsComponent } from './components/print-results/print-results.component';
import { AllFacetsComponent } from './facets/all-facets/all-facets.component';
import { EmailProductsComponent } from './components/email-products/email-products.component';
import { PrintAreaComponent } from './components/print-area/print-area.component';
import { ProductFeedbackComponent } from './components/product-feedback/product-feedback.component';
import { CitationComponent } from './components/citation/citation.component';
import { ThumbnailLoaderComponent } from './components/thumbnail-loader/thumbnail-loader.component';
import { AiExtractionComponent } from './ai/ai-extraction/ai-extraction.component';
import { SaveLocationComponent } from './map/save-location/save-location.component';
import { SaveSearchComponent } from './saved-search/save-search/save-search.component';
import { SavedSearchesComponent } from './saved-search/saved-searches/saved-searches.component';
import { SearchbarComponent } from './layout/searchbar/searchbar.component';
import { SearchComponent } from './search/search/search.component';
import { LogsComponent } from './workflow/components/logs/logs.component';
import { BatchManagementComponent } from './workflow/components/batch-management/batch-management.component';
import { ReferralManagementComponent } from './workflow/components/referral-management/referral-management.component';
import { ReferralAddComponent } from './workflow/components/referral-add/referral-add.component';
import { TaskManagementComponent } from './workflow/components/task-management/task-management.component';
import { TaskDirtyManagementComponent } from './workflow/components/task-dirty-management/task-dirty-management.component';
import { TaskCompletionComponent } from './workflow/components/task-completion/task-completion.component';
import { BatchPropertiesComponent } from './workflow/components/batch-properties/batch-properties.component';
import { TaskReferralPropertiesComponent } from './workflow/components/task-referral-properties/task-referral-properties.component';
import { BatchDocumentProcessDiagramComponent } from './workflow/components/batch-document-process-diagram/batch-document-process-diagram.component';
import { EditorQueriesComponent } from './workflow/components/editor-queries/editor-queries.component';
import { TaskPropertiesComponent } from './workflow/components/task-properties/task-properties.component';
import { BatchDocumentsComponent } from './workflow/components/batch-documents/batch-documents.component';
import { BatchAddComponent } from './workflow/components/batch-add/batch-add.component';
import { ReferralsComponent } from './workflow/components/referrals/referrals.component';
import { UnclaimedTasksComponent } from './workflow/components/unclaimed-tasks/unclaimed-tasks.component';
import { MyTasksComponent } from './workflow/components/my-tasks/my-tasks.component';
import { BatchesComponent } from './workflow/components/batches/batches.component';
import { WorkflowMainComponent } from './workflow/components/workflow-main/workflow-main.component';
import { AnnotationEditorComponent } from './workflow/components/annotation-editor/annotation-editor.component';
import { QuerySelectorAgComponent } from './workflow/components/query-selector-ag/query-selector-ag.component';
import { QuerylistSelectorAgComponent } from './workflow/components/querylist-selector-ag/querylist-selector-ag.component';
import { NerSelectorAgComponent } from './workflow/components/ner-selector-ag/ner-selector-ag.component';
import { BatchQueriesAgComponent } from './workflow/components/batch-queries-ag/batch-queries-ag.component';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { XsrfInterceptor } from './services/xsrf-interceptor.service';
import { DownloadsComponent } from './download/downloads/downloads.component';
import { AlbumComponent } from './albums/album/album.component';
import { AlbumShareComponent } from './albums/album-share/album-share.component';
import { AlbumsComponent } from './albums/albums/albums.component';
import { AlbumItemComponent } from './albums/album-item/album-item.component';
import { AuditComponent } from './records-management/components/audit/audit.component';
import { RecordQuickViewComponent } from './records-management/components/record-quick-view/record-quick-view.component';
import { MetadataQuickViewComponent } from './views/metadata-quick-view/metadata-quick-view.component';
import { CartComponent } from './cart/cart/cart.component';
import { CartPurchasesComponent } from './cart/cart/cart-purchases.component';
import { BlockUIWrapperComponent } from './layout/block-ui/block-ui-wrapper.component';
import { CartCheckoutCompleteComponent } from './cart/cart/cart-checkout-complete.component';
import { ProductShareComponent } from './share/product-share/product-share.component';
import { EmptyViewComponent } from './views/empty-view/empty-view.component';
import { TagsComponent } from './components/tags/tags.component';
import { RecordFilingComponent } from './records-management/components/record-filing/record-filing.component';
import { CookiesComponent } from './components/cookies/cookies.component';
import { FooterComponent } from './layout/footer/footer.component';
import { RecordViewComponent } from './records-management/components/record-view/record-view.component';
import { ListViewComponent } from './views/list-view/list-view.component';
import { ThumbnailViewComponent } from './views/thumbnail-view/thumbnail-view.component';
import { FaqComponent } from './components/faq/faq.component';
import { IdleComponent } from './idle/idle/idle.component';
import { RevisionHistoryComponent } from './components/revision-history/revision-history.component';
import { PasswordResetComponent } from './login/password-reset/password-reset.component';
import { LoginComponent } from './login/login/login.component';
import { LoginModalComponent } from './login/login-modal/login-modal.component';
import { CopyLinkComponent } from './components/copy-link/copy-link.component';
import { BrowseViewComponent } from './browse/components/browse-view/browse-view.component';
import { BrowseEditComponent } from './browse/components/browse-edit/browse-edit.component';
import { MessagesComponent } from './user-preferences/messages/messages.component';
import { DetailsComponent } from './user-preferences/details/details.component';
import { UploadComponent } from './upload/upload/upload.component';
import { LibrariesComponent } from './user-preferences/libraries/libraries.component';
import { PasswordComponent } from './user-preferences/password/password.component';
import { SearchOptsComponent } from './user-preferences/search-opts/search-opts.component';
import { InternationalOptsComponent } from './user-preferences/international-opts/international-opts.component';
import { DisplayOptsComponent } from './user-preferences/display-opts/display-opts.component';
import { ResultsOptsComponent } from './user-preferences/results-opts/results-opts.component';
import { FieldsComponent } from './user-preferences/fields/fields.component';
import { ProfileComponent } from './user-preferences/profile/profile.component';
import { GridViewComponent } from './views/grid-view/grid-view.component';
import { EmailModalComponent } from './email/email-modal/email-modal.component';
import { GridCellRendererComponent } from './views/grid-cell-renderer/grid-cell-renderer.component';
import { GridTitleCellRendererComponent } from './views/grid-title-cell-renderer/grid-title-cell-renderer.component';
import { GridCheckboxCellRendererComponent } from './views/grid-checkbox-cell-renderer/grid-checkbox-cell-renderer.component';
import { GridLinkCellRendererComponent } from './views/grid-link-cell-renderer/grid-link-cell-renderer.component';
import { GridSearchableCellRendererComponent } from './views/grid-searchable-cell-renderer/grid-searchable-cell-renderer.component';
import { GridMenuCellRendererComponent } from './views/grid-menu-cell-renderer/grid-menu-cell-renderer.component';
import { GridIconsCellRendererComponent } from './views/grid-icons-cell-renderer/grid-icons-cell-renderer.component';
import { GridToolsHeaderRendererComponent } from './views/grid-tools-header-renderer/grid-tools-header-renderer.component';
import { MySearchesComponent } from './saved-search/my-searches/my-searches.component';
import { FacetsComponent } from './facets/facets/facets.component';
import { SavedLocationsComponent } from './map/saved-locations/saved-locations.component';
import { ImportLocationsComponent } from './map/import-locations/import-locations.component';
import { SearchHistoryComponent } from './saved-search/search-history/search-history.component';
import { GeoLocateComponent } from './map/geo-locate/geo-locate.component';
import { MapResultsViewComponent } from './map/map-results-view/map-results-view.component';
import { AdvancedComponent } from './layout/advanced/advanced.component';
import { SrsFieldComponent } from './fields/srs-field/srs-field.component';
import { CollectionComponent } from './fields/collection/collection.component';
import { HomeComponent } from './layout/home/home.component';
import { DashboardComponent } from './layout/dashboard/dashboard.component';
import { MetadataViewComponent } from './views/metadata-view/metadata-view.component';
import { AlbumProductInfoComponent } from './albums/album-product-info/album-product-info.component';
import { MetadataEditComponent } from './edit/metadata-edit/metadata-edit.component';
import { GlobalEditComponent } from './edit/global-edit/global-edit.component';
import { EditFieldComponent } from './edit/edit-field/edit-field.component';
import { EditFieldCollectionComponent } from './edit/edit-field-collection/edit-field-collection.component';
import { DateValidateDirective } from './directives/date-validate.directive';
import { DateValidateMinMaxDirective } from './directives/date-validate-min-max.directive';
import { AutoExpandDirective } from './directives/auto-expand.directive';
import { AutoLoginComponent } from './login/auto-login/auto-login.component';
import { DocViewComponent } from './views/doc-view/doc-view.component';
import { NavigationComponent } from './layout/navigation/navigation.component';
import { DocPdfViewComponent } from './views/doc-pdf-view/doc-pdf-view.component';
import { DocMetadataViewComponent } from './views/doc-metadata-view/doc-metadata-view.component';
import { DocTextViewComponent } from './views/doc-text-view/doc-text-view.component';
import { DocAudioViewComponent } from './views/doc-audio-view/doc-audio-view.component';
import { DocVideoViewComponent } from './views/doc-video-view/doc-video-view.component';
import { KvVideoDirective } from './directives/kv-video.directive';
import { DocImageViewComponent } from './views/doc-image-view/doc-image-view.component';
import { DiffViewerComponent } from './components/diff-viewer/diff-viewer.component';
import { MapViewComponent } from './map/map-view/map-view.component';
import { GeoTagComponent } from './map/geo-tag/geo-tag.component';
import { GeoReferenceComponent } from './map/geo-reference/geo-reference.component';
import { FaqWrapperComponent } from './wrapper/faq-wrapper/faq-wrapper.component';
import { HomeWrapperComponent } from './wrapper/home-wrapper/home-wrapper.component';
import { SearchWrapperComponent } from './wrapper/search-wrapper/search-wrapper.component';
import { CartWrapperComponent } from './wrapper/cart-wrapper/cart-wrapper.component';
import { PurchasesWrapperComponent } from './wrapper/purchases-wrapper/purchases-wrapper.component';
import { ProfileWrapperComponent } from './wrapper/profile-wrapper/profile-wrapper.component';
import { ResetWrapperComponent } from './wrapper/reset-wrapper/reset-wrapper.component';
import { CheckoutWrapperComponent } from './wrapper/checkout-wrapper/checkout-wrapper.component';
import { CheckoutCompleteWrapperComponent } from './wrapper/checkout-complete-wrapper/checkout-complete-wrapper.component';
import { DownloadOrderWrapperComponent } from './wrapper/download-order-wrapper/download-order-wrapper.component';
import { DownloadsWrapperComponent } from './wrapper/downloads-wrapper/downloads-wrapper.component';
import { SavedSearchesWrapperComponent } from './wrapper/saved-searches-wrapper/saved-searches-wrapper.component';
import { UploadWrapperComponent } from './wrapper/upload-wrapper/upload-wrapper.component';
import { AlbumsWrapperComponent } from './wrapper/albums-wrapper/albums-wrapper.component';
import { AutologinwithcriteriaWrapperComponent } from './wrapper/autologinwithcriteria-wrapper/autologinwithcriteria-wrapper.component';
import { DxMainWrapperComponent } from './wrapper/dx-main-wrapper/dx-main-wrapper.component';
import { LogoutSplashWrapperComponent } from './wrapper/logout-splash-wrapper/logout-splash-wrapper.component';
import { LoginWrapperComponent } from './wrapper/login-wrapper/login-wrapper.component';
import { AutologinWrapperComponent } from './wrapper/autologin-wrapper/autologin-wrapper.component';
import { LogoutSplashComponent } from './login/logout-splash/logout-splash.component';
import { DraggableElemDirective } from './directives/draggable-elem.directive';
import { MapRecordPreviewComponent } from './map/map-record-preview/map-record-preview.component';
import { DocGeoViewComponent } from './views/doc-geo-view/doc-geo-view.component';
import { GridGeoFeatureToolsCellRendererComponent } from './views/grid-geo-feature-tools-cell-renderer/grid-geo-feature-tools-cell-renderer.component';
import { ViewerResizerDirective } from './directives/viewer-resizer.directive';
import { UsOfficialBannerComponent } from './layout/custom/us-official-banner/us-official-banner.component';
import { DocMemorialViewComponent } from './views/custom/doc-memorial-view/doc-memorial-view.component';
import { MemorialPrintComponent } from './components/custom/memorial-print/memorial-print.component';
import { BrandBannerComponent } from './layout/custom/brand-banner/brand-banner.component';
import { BrandEmptyViewComponent } from './views/custom/brand-empty-view/brand-empty-view.component';
import { AddPagesModalComponent } from './cart/add-pages-modal/add-pages-modal.component';
import { ThemeBuilderComponent } from './theme/theme-builder/theme-builder.component';
import { GroupByKeyPipe } from './pipes/group-by-key.pipe';

@NgModule({
  declarations: [
	SafeHtmlPipe,
	FacetHideFilterPipe,
	FacetRefineFilterPipe,
	LimitToPipe,
	IsEmptyPipe,
	FormatStringPipe,
	AppComponent,
    AboutComponent,
	ConfirmComponent,
	MassDownloadComponent,
 	SearchTermsComponent,
  	ImageRotateComponent,
    LibraryMoveComponent,
    PrintResultsComponent,
    AllFacetsComponent,
    EmailProductsComponent,
    PrintAreaComponent,
    SaveLocationComponent,
    ProductFeedbackComponent,
    TextCollapseDirective,
    SaveSearchComponent,
    CitationComponent,
    CitationDirective,
    LogsComponent,
    AiExtractionComponent,
    AlertsComponent,
    DownloadsComponent,
    AlbumComponent,
    AlbumShareComponent,
    AlbumsComponent,
    AlbumItemComponent,
    ThumbnailLoaderComponent,
    DropzoneDirective,
    FileinputDirective,
    AuditComponent,
    RecordQuickViewComponent,
    MetadataQuickViewComponent,
    BatchManagementComponent,
    ReferralManagementComponent,
    ReferralAddComponent,
    TaskManagementComponent,
    TaskDirtyManagementComponent,
    TaskCompletionComponent,
    BatchPropertiesComponent,
    TaskReferralPropertiesComponent,
    BatchDocumentProcessDiagramComponent,
    EditorQueriesComponent,
    TaskPropertiesComponent,
    BatchDocumentsComponent,
    BatchAddComponent,
    ReferralsComponent,
    UnclaimedTasksComponent,
    MyTasksComponent,
    BatchesComponent,
    ProductShareComponent,
    EmptyViewComponent,
    SavedSearchesComponent,
    SearchComponent,
    SearchbarComponent,
    TagsComponent,
    CartComponent,
    CartPurchasesComponent,
    CartCheckoutCompleteComponent,
    BlockUIWrapperComponent,
    RecordFilingComponent,
    CookiesComponent,
    FooterComponent,
    RecordViewComponent,
    ListViewComponent,
    ThumbnailViewComponent,
    FaqComponent,
    IdleComponent,
    RevisionHistoryComponent,
    PasswordResetComponent,
    LoginComponent,
    LoginModalComponent,
	ZoomImageDirective,
	OverflowHideDirective,
	CaSidebarDirective,
	WorkflowMainComponent,
	AnnotationEditorComponent,
	CopyLinkComponent,
	BrowseViewComponent,
	BrowseEditComponent,
    MessagesComponent,
    DetailsComponent,
    UploadComponent,
    BrowsePathValidateDirective,
    LibrariesComponent,
    PasswordComponent,
    SearchOptsComponent,
    InternationalOptsComponent,
    DisplayOptsComponent,
    ResultsOptsComponent,
    FieldsComponent,
    ProfileComponent,
    GridViewComponent,
    QuerySelectorAgComponent,
    QuerylistSelectorAgComponent,
    NerSelectorAgComponent,
    BatchQueriesAgComponent,
    EmailModalComponent,
    GridCellRendererComponent,
    GridTitleCellRendererComponent,
    GridCheckboxCellRendererComponent,
    GridLinkCellRendererComponent,
    GridSearchableCellRendererComponent,
    GridMenuCellRendererComponent,
    GridIconsCellRendererComponent,
    MySearchesComponent,
    FacetsComponent,
    SavedLocationsComponent,
    ImportLocationsComponent,
    SearchHistoryComponent,
    GeoLocateComponent,
    MapResultsViewComponent,
    AdvancedComponent,
    SrsFieldComponent,
    CollectionComponent,
    HomeComponent,
    DashboardComponent,
    MetadataViewComponent,
    AlbumProductInfoComponent,
    MetadataEditComponent,
    GlobalEditComponent,
    EditFieldComponent,
    EditFieldCollectionComponent,
    DateValidateDirective,
    DateValidateMinMaxDirective,
    AutoExpandDirective,
    DocViewComponent,
    NavigationComponent,
    DocPdfViewComponent,
    DocMetadataViewComponent,
    DocTextViewComponent,
    DocAudioViewComponent,
    DocVideoViewComponent,
    KvVideoDirective,
    DocImageViewComponent,
    DiffViewerComponent,
    GridToolsHeaderRendererComponent,
    MapViewComponent,
    GeoTagComponent,
    GeoReferenceComponent,
    FaqWrapperComponent,
    HomeWrapperComponent,
    SearchWrapperComponent,
    CartWrapperComponent,
    PurchasesWrapperComponent,
    ProfileWrapperComponent,
    ResetWrapperComponent,
    CheckoutWrapperComponent,
    CheckoutCompleteWrapperComponent,
    DownloadOrderWrapperComponent,
    DownloadsWrapperComponent,
    SavedSearchesWrapperComponent,
    UploadWrapperComponent,
    AlbumsWrapperComponent,
    AutologinwithcriteriaWrapperComponent,
    DxMainWrapperComponent,
    LogoutSplashWrapperComponent,
    LoginWrapperComponent,
    AutoLoginComponent,
    AutologinWrapperComponent,
    LogoutSplashComponent,
    DraggableElemDirective,
    MapRecordPreviewComponent,
    DocGeoViewComponent,
    GridGeoFeatureToolsCellRendererComponent,
    ViewerResizerDirective,
    UsOfficialBannerComponent,
    DocMemorialViewComponent,
    MemorialPrintComponent,
    BrandBannerComponent,
    BrandEmptyViewComponent,
    AddPagesModalComponent,
    ThemeBuilderComponent,
    GroupByKeyPipe
  ],
  imports: [
	BrowserModule, 
	AppRoutingModule,
	HttpClientModule,
	FormsModule,
	NgbModule,
	NgbPaginationModule,
	NgbDropdownModule,
	NgbNavModule,
	NgbTypeaheadModule,
	NgSelectModule,
	CommonModule,
	// Toast global config
	ToastrModule.forRoot({
	  positionClass: 'toast-bottom-right',
	  timeOut: 3000,
	  closeButton: true,
	  progressBar: false,
	  preventDuplicates: true,
	  enableHtml: true
	}),
	BrowserAnimationsModule,
	AgGridModule,
	AngularCropperjsModule,
	BlockUIModule.forRoot(),
	NgIdleKeepaliveModule.forRoot()
  ],
  exports: [
	TextCollapseDirective,
	CitationDirective
  ],
  providers: [
	{
	  provide: APP_INITIALIZER,
	  useFactory: initializeApp,
	  deps: [ConfigService, UserService, AnalyticsService, LoginService, FeatureService, Router],
	  multi: true,
	},	
	{
      provide: HTTP_INTERCEPTORS,
      useClass: XsrfInterceptor,
      multi: true,
    }
  ],
  bootstrap: [AppComponent]
})
export class AppModule {}
